import { ArrowLeft, Layers, Library, Plus } from 'lucide-react'
import { useEffect, type ReactNode } from 'react'
import { Link, useOutletContext } from 'react-router-dom'
import { Button, Tabs } from '../../../components/ui'
import type { AdminOutletContext } from '../../../layouts/AdminLayout'
import styles from '../../../pages/ToolsPage.module.css'

const cx = (...classes: Array<string | false | null | undefined>): string =>
  classes
    .filter(Boolean)
    .map((className) => styles[className as string] ?? className)
    .join(' ')

type PromptManagementShellProps = {
  actionLabel: string
  children: ReactNode
  onAction: () => void
}

export const PromptManagementShell = ({
  actionLabel,
  children,
  onAction
}: PromptManagementShellProps) => {
  const { closeMobileActions, setMobileMenuItems } = useOutletContext<AdminOutletContext>()

  useEffect(() => {
    setMobileMenuItems(
      <>
        <Link
          className="mobile-actions-item"
          onClick={closeMobileActions}
          role="menuitem"
          to="/admin/tools/prompt-management"
        >
          <Library size={17} />
          <span>Library</span>
        </Link>
        <Link
          className="mobile-actions-item"
          onClick={closeMobileActions}
          role="menuitem"
          to="/admin/tools/prompt-management/groups"
        >
          <Layers size={17} />
          <span>Groups</span>
        </Link>
        <button
          className="mobile-actions-item"
          onClick={() => {
            closeMobileActions()
            onAction()
          }}
          role="menuitem"
          type="button"
        >
          <Plus size={17} />
          <span>{actionLabel}</span>
        </button>
      </>
    )

    return () => setMobileMenuItems(null)
  }, [actionLabel, closeMobileActions, onAction, setMobileMenuItems])

  return (
    <>
      <div className={cx('page-header', styles['tools-page-header'], styles['tools-page-header-detail'])}>
        <div className={styles['tools-header-copy']}>
          <Link className={styles['tools-back-link']} to="/admin/tools">
            <ArrowLeft size={16} />
            <span>Tools</span>
          </Link>
          <h1 className="page-title">Prompt Library</h1>
          <p className="page-subtitle">Manage reusable Suno style prompts and organize them into groups.</p>
        </div>
        <div className={cx('page-header-actions', styles['prompt-header-actions'])}>
          <Tabs
            ariaLabel="Prompt Library sections"
            className={cx(styles['prompt-management-tabs'])}
            items={[
              { label: 'Library', to: '/admin/tools/prompt-management' },
              { label: 'Groups', to: '/admin/tools/prompt-management/groups' }
            ]}
          />
          <Button className={cx(styles['prompt-create-button'])} mobileFullWidth onClick={onAction} variant="primary">
            <Plus size={16} /> {actionLabel}
          </Button>
        </div>
      </div>

      <section className={cx('prompt-management-page', styles['prompt-management'])} aria-label="Prompt management">
        {children}
      </section>
    </>
  )
}
